// Conversor bidirecional HTML ↔ markdown canônico do banco de questões.
// [[project-question-wysiwyg-editor]]
//
// Canonical markdown que o `renderInline`/`renderBlock` (./markdown) renderiza:
//   **bold** *italic* __bold__ _italic_
//   ==realce== ++sublinhado++ ~~tachado~~
//   `code`
//   [label](http://url) — só http/https
//   {{color:nome}}texto{{/color}} — paleta nomeada (azul, vermelho, etc.)
//   \(...\)  $...$           — math inline
//   $$...$$  \[...\]         — math display (cross-line)
//   \n\n entre parágrafos, \n quebra simples dentro do mesmo parágrafo
//
// Imagens e tabelas NÃO ficam inline no texto canônico. Ficam em arrays
// dedicados (`imagens[]`, `tabelas[]`) — o WYSIWYG insere placeholders
// `[img:N]` e `[tabela:N]` no texto e o caller faz a coordenação com os
// arrays separados.
//
// Estratégia: parser DOM nativo (DOMParser). Walk recursivo do tree, emitindo
// markdown por nó. Não dá pra usar regex direto porque o contentEditable do
// browser produz HTML que mistura inline e block (ex: <p><strong>X</strong></p>).
//
// Idempotência: htmlToCanonicalMd(canonicalMdToHtml(md)) ≈ md. A perda
// aceitável é normalização de espaços e ordem de marcadores aninhados
// (ex: ~~**X**~~ vs **~~X~~**).

import { COLOR_TOKENS, normalizeColorName } from "./markdown";

// ---------------------------------------------------------------------------
// Hex (lowercased) → nome canônico da paleta. Inverte COLOR_TOKENS, escolhendo
// o PRIMEIRO nome canônico que mapeia pro hex.

const HEX_TO_NAME = ((): Map<string, string> => {
  const m = new Map<string, string>();
  for (const [name, hex] of Object.entries(COLOR_TOKENS)) {
    const key = hex.toLowerCase();
    if (!m.has(key)) m.set(key, name);
  }
  return m;
})();

function hexFromColorStr(color: string): string | null {
  // aceita "#rrggbb", "rgb(r, g, b)", "rgb(r g b)"
  const trimmed = color.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    const r = trimmed[1],
      g = trimmed[2],
      b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  const rgb = trimmed.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (rgb) {
    const toHex = (n: string) => {
      const v = Math.max(0, Math.min(255, parseInt(n, 10)));
      return v.toString(16).padStart(2, "0");
    };
    return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`;
  }
  return null;
}

function colorNameForStyle(color: string): string | null {
  const hex = hexFromColorStr(color);
  if (!hex) return null;
  return HEX_TO_NAME.get(hex) ?? null;
}

// ---------------------------------------------------------------------------
// htmlToCanonicalMd

interface ConvertOpts {
  /** Quando true, mantém placeholders `[img:N]` em vez de tirar a entrada
   *  da árvore. Default true — caller resolve N depois. */
  keepImagePlaceholders?: boolean;
  keepTablePlaceholders?: boolean;
  /** Se true, emite `<br>` no markdown como `  \n` (Markdown soft break).
   *  Default: `\n` simples — o `renderInline` quebra por `\n` dentro do
   *  parágrafo. */
  brAsHardBreak?: boolean;
}

export function htmlToCanonicalMd(
  html: string,
  opts: ConvertOpts = {}
): string {
  if (!html.trim()) return "";
  const parser = new DOMParser();
  // Wrap em div pra DOMParser não simplificar texto solto
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement;
  return walkBlock(root, opts).trim();
}

function walkBlock(node: HTMLElement, opts: ConvertOpts): string {
  const parts: string[] = [];
  for (const child of Array.from(node.childNodes)) {
    parts.push(emitNode(child, opts, "block"));
  }
  // Junta com newline duplo entre blocos. Trim por bloco já foi feito.
  return parts.join("").replace(/\n{3,}/g, "\n\n");
}

function emitNode(
  node: Node,
  opts: ConvertOpts,
  ctx: "block" | "inline"
): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeMdLiteral(node.textContent ?? "");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  // ---- Math (rendered span com data-tex contém a source canônica)
  if (
    el.classList.contains("math-inline") ||
    el.classList.contains("katex") ||
    (tag === "span" && el.dataset.tex)
  ) {
    const src = el.dataset.tex ?? el.getAttribute("data-tex") ?? "";
    return src ? `\\(${src}\\)` : "";
  }
  if (el.classList.contains("math-display") && el.dataset.tex) {
    return `\n\n$$${el.dataset.tex}$$\n\n`;
  }

  // ---- Imagens
  //   - <img data-img-idx="N">  → placeholder [img:N] (caller mantém array)
  //   - <img src="data:..."> ou http(s)  → markdown inline ![alt](src) pra que
  //     o conteúdo do textarea legacy do VisualBankEditor (que salva inline)
  //     continue compatível bidirecionalmente. [[project-question-wysiwyg-editor]]
  if (tag === "img") {
    if (opts.keepImagePlaceholders === false) return "";
    const idx = el.dataset.imgIdx;
    if (idx != null) return `[img:${idx}]`;
    const src = el.getAttribute("src") ?? "";
    if (!src) return "";
    const alt = (el.getAttribute("alt") ?? "imagem").replace(/[\]]/g, "");
    return `![${alt}](${src})`;
  }

  // ---- Tabelas — idem
  if (tag === "table") {
    if (opts.keepTablePlaceholders === false) return "";
    const idx = el.dataset.tableIdx;
    if (idx != null) return `[tabela:${idx}]`;
    return "";
  }

  // ---- Blocos
  if (tag === "p") {
    const inner = walkInline(el, opts);
    return inner ? `${inner}\n\n` : "";
  }
  if (tag === "br") {
    return ctx === "block" ? "" : opts.brAsHardBreak ? "  \n" : "\n";
  }
  if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4") {
    const level = parseInt(tag[1], 10);
    const inner = walkInline(el, opts);
    return inner ? `${"#".repeat(level)} ${inner}\n\n` : "";
  }
  if (tag === "blockquote") {
    const inner = walkBlock(el, opts).trim();
    if (!inner) return "";
    return inner
      .split("\n")
      .map((l) => (l.trim() ? `> ${l}` : ">"))
      .join("\n") + "\n\n";
  }
  if (tag === "ul") {
    const items = Array.from(el.children)
      .filter((c) => c.tagName.toLowerCase() === "li")
      .map((li) => {
        const isTask = (li as HTMLElement).classList.contains("task-list-item");
        if (isTask) {
          const cb = li.querySelector('input[type="checkbox"]');
          const checked = cb && (cb as HTMLInputElement).checked;
          const inner = walkInline(li as HTMLElement, opts, {
            skipFirstCheckbox: true
          });
          return `- [${checked ? "x" : " "}] ${inner}`;
        }
        return `- ${walkInline(li as HTMLElement, opts)}`;
      })
      .filter((s) => s.length > 2)
      .join("\n");
    return items ? `${items}\n\n` : "";
  }
  if (tag === "ol") {
    const items = Array.from(el.children)
      .filter((c) => c.tagName.toLowerCase() === "li")
      .map((li, i) => `${i + 1}. ${walkInline(li as HTMLElement, opts)}`)
      .filter((s) => s.length > 3)
      .join("\n");
    return items ? `${items}\n\n` : "";
  }
  if (tag === "hr") return "\n---\n\n";
  if (tag === "pre") {
    const text = (el.textContent ?? "").replace(/\n+$/, "");
    return text ? `\n\`\`\`\n${text}\n\`\`\`\n\n` : "";
  }

  // ---- Inline
  if (tag === "strong" || tag === "b") {
    const inner = walkInline(el, opts);
    return inner ? `**${inner}**` : "";
  }
  if (tag === "em" || tag === "i") {
    const inner = walkInline(el, opts);
    return inner ? `*${inner}*` : "";
  }
  if (tag === "u") {
    const inner = walkInline(el, opts);
    return inner ? `++${inner}++` : "";
  }
  if (tag === "s" || tag === "strike" || tag === "del") {
    const inner = walkInline(el, opts);
    return inner ? `~~${inner}~~` : "";
  }
  if (tag === "mark") {
    const inner = walkInline(el, opts);
    return inner ? `==${inner}==` : "";
  }
  if (tag === "code" && el.parentElement?.tagName.toLowerCase() !== "pre") {
    return `\`${el.textContent ?? ""}\``;
  }
  if (tag === "a") {
    const href = el.getAttribute("href") ?? "";
    const label = walkInline(el, opts);
    if (href && /^https?:\/\//i.test(href)) {
      return `[${label}](${href})`;
    }
    return label;
  }
  if (tag === "span") {
    // Color span: style="color: #xxx" ou class indicando paleta
    const styleColor = el.style.color || "";
    const dataColor = el.dataset.color || "";
    const name =
      (dataColor && normalizeColorName(dataColor)) ||
      (styleColor && colorNameForStyle(styleColor));
    const inner = walkInline(el, opts);
    if (name) return `{{color:${name}}}${inner}{{/color}}`;
    return inner;
  }
  if (tag === "div") {
    // div pode aparecer como pseudo-bloco do contentEditable; trata como
    // parágrafo virtual quando tem conteúdo.
    const inner = ctx === "block" ? walkBlock(el, opts) : walkInline(el, opts);
    if (ctx === "block") {
      const trimmed = inner.trim();
      return trimmed ? `${trimmed}\n\n` : "";
    }
    return inner;
  }

  // Default: emite só o conteúdo
  return ctx === "block" ? walkBlock(el, opts) : walkInline(el, opts);
}

interface InlineOpts {
  skipFirstCheckbox?: boolean;
}

function walkInline(
  el: HTMLElement,
  opts: ConvertOpts,
  inlineOpts: InlineOpts = {}
): string {
  let out = "";
  let skipped = false;
  for (const child of Array.from(el.childNodes)) {
    if (
      inlineOpts.skipFirstCheckbox &&
      !skipped &&
      child.nodeType === Node.ELEMENT_NODE &&
      (child as HTMLElement).tagName.toLowerCase() === "input"
    ) {
      skipped = true;
      continue;
    }
    out += emitNode(child, opts, "inline");
  }
  return out.trim();
}

// Escapes para texto literal — evita que asteriscos/underscores/colchetes em
// texto comum sejam interpretados como markdown na próxima passagem.
function escapeMdLiteral(s: string): string {
  // Conservador: só os 4 mais perigosos. Não escapamos `*` e `_` porque
  // muito conteúdo legítimo (variáveis x_1, fórmulas) usaria escape demais.
  // Aceito risco: round-trip pode "comer" um asterisco solto se ele já
  // virou <em> no contentEditable.
  return s
    .replace(/\\/g, "\\\\")
    .replace(/\[(?=img:\d+\]|tabela:\d+\])/g, "\\["); // preserva nossos placeholders
}

// ---------------------------------------------------------------------------
// canonicalMdToHtml — usa o render existente do app, depois pós-processa pra:
//   - substituir `[img:N]` por <img data-img-idx="N"> (carregando do array
//     de imagens da questão, passado pelo caller via map idx→dataUrl)
//   - substituir `[tabela:N]` por uma representação visual da tabela do
//     array passado pelo caller
//
// Esses passes são opcionais — quem só quer o HTML "puro" do texto chama com
// resolvers undefined.

export interface MdToHtmlOpts {
  /** idx → dataUrl/url da imagem. Quando ausente, deixa o placeholder
   *  literal `[img:N]` no HTML pra autor ver. */
  resolveImage?: (idx: number) => { url: string; alt?: string } | null;
  /** idx → markup pré-renderizado da tabela. */
  resolveTable?: (idx: number) => string | null;
}

const IMG_PLACEHOLDER_RE = /\[img:(\d+)\]/g;
const TABLE_PLACEHOLDER_RE = /\[tabela:(\d+)\]/g;

export function expandPlaceholders(
  html: string,
  opts: MdToHtmlOpts = {}
): string {
  let out = html;
  if (opts.resolveImage) {
    out = out.replace(IMG_PLACEHOLDER_RE, (_m, n: string) => {
      const idx = Number(n);
      const r = opts.resolveImage!(idx);
      if (!r) return `<span class="text-amber-600">[img:${idx}]</span>`;
      const alt = (r.alt ?? "").replace(/"/g, "&quot;");
      return `<img data-img-idx="${idx}" src="${r.url}" alt="${alt}" class="mx-auto my-2 max-w-full rounded-md border" contenteditable="false" />`;
    });
  }
  if (opts.resolveTable) {
    out = out.replace(TABLE_PLACEHOLDER_RE, (_m, n: string) => {
      const idx = Number(n);
      const markup = opts.resolveTable!(idx);
      if (!markup) return `<span class="text-amber-600">[tabela:${idx}]</span>`;
      // Garante data-table-idx pra ida-volta
      return markup.replace(
        /^<table\b/,
        `<table data-table-idx="${idx}" contenteditable="false"`
      );
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// extractAndDecomposeImages — quando o usuário cola uma imagem dentro do
// contentEditable, intercepta o `<img src="data:...">` e devolve o dataUrl
// + posição (placeholder). Caller insere no array `imagens[]` e troca o
// `<img>` por `<img data-img-idx="N">` apontando pro índice.

export interface ExtractedImage {
  dataUrl: string;
  alt: string;
}

/** Recebe um fragmento HTML (clipboard paste) e retorna lista de data-URLs
 *  encontrados. Útil pra paste handler decidir se vai bloquear ou aceitar.  */
export function extractDataImagesFromHtml(html: string): ExtractedImage[] {
  const out: ExtractedImage[] = [];
  const re = /<img\b[^>]*\bsrc\s*=\s*"(data:image\/[^";]+;base64,[^"]+)"[^>]*>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const altMatch = m[0].match(/\balt\s*=\s*"([^"]*)"/i);
    out.push({ dataUrl: m[1], alt: altMatch?.[1] ?? "" });
  }
  return out;
}
